import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Accordion,
    Button,
    Card,
    Container,
    Dropdown,
    Form,
    Modal,
    Spinner,
} from "react-bootstrap";
import { AiOutlineClear } from "react-icons/ai";
import {
    FaAngleLeft,
    FaCopy,
    FaExclamationTriangle,
    FaFastBackward,
    FaPen,
    FaPlus,
    FaSave,
    FaTrash,
} from "react-icons/fa";
import { MdOutlineMoreVert } from "react-icons/md";
import { useNavigate, useParams } from "react-router";
import { generate } from "short-uuid";

import * as api from "../../api/api";
import {
    LocationBuilderAreasDocument,
    useAreasDocument,
    useLocationBuilderAreasDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import buildAreas from "../../components/AreaBuilder/index.jsx";
import AreaSettingsCard from "../../components/AreaSettingsCard";
import CancelButton from "../../components/CancelButton";
import Header from "../../components/Header/Header";
import {
    ConfigDownloadJSONButton,
    ConfigUploadJSONButton,
} from "../../components/JsonButtons/index.jsx";
import { Title } from "../../components/Layout/Layout";
import CorvusMap from "../../components/Map";
import urls from "../../urls.jsx";

function deepEqual(a, b) {
    if (a === b) return true;

    if (
        typeof a !== "object" ||
        a === null ||
        typeof b !== "object" ||
        b === null
    ) {
        return false;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!deepEqual(a[key], b[key])) return false;
    }

    return true;
}

const PageState = {
    BOOT: "boot",
    DEFAULTS: "defaults",
    SETTINGS: "settings",
    CONFLICT: "conflict",
};

export const BuilderAreasRelation = {
    NO_BUILDER_YES_AREAS: "no_builder_yes_areas",
    NO_BUILDER_NO_AREAS: "no_builder_no_areas",
    MATCH: "match",
    CONFLICT: "conflict",
};

const defaultValues = {
    columnParams: {
        amount: 3,
        size: 3,
        startOffset: 0,
        spacing: 0,
        skewness: 0,

        scheme: "numeric",
        capitalization: "uppercase",
        initial: 1,
        stepSize: 1,
        order: "true",
        prefix: "Kap ",
        suffix: "",
        zeroPad: 0,
    },
    rowParams: {
        amount: 3,
        size: 3,
        startOffset: 0,
        spacing: 0,
        skewness: 0,

        scheme: "numeric",
        capitalization: "uppercase",
        initial: 1,
        stepSize: 1,
        order: "true",
        prefix: "",
        suffix: "",
        zeroPad: 0,
    },
    z: 0.0,
};

function applyDefaults(target, defaults) {
    for (const key in defaults) {
        if (
            typeof defaults[key] === "object" &&
            defaults[key] !== null &&
            !Array.isArray(defaults[key])
        ) {
            if (typeof target[key] !== "object" || target[key] === null) {
                target[key] = {};
            }
            applyDefaults(target[key], defaults[key]);
        } else {
            if (!(key in target)) {
                target[key] = defaults[key];
            }
        }
    }
}

function normalizeLocationBuilderSettings(doc, defaultValues) {
    if (!doc) return null;

    let builderSettings;

    // New format
    if (Array.isArray(doc.settings)) {
        builderSettings = doc.settings;
    }

    // Old format
    else {
        const clone = structuredClone(doc);
        delete clone.id;
        builderSettings = Object.values(clone);
    }

    return builderSettings.map((s) => {
        const normalized = structuredClone(s);
        applyDefaults(normalized, defaultValues);

        if (!normalized.id) {
            normalized.id = generate();
        }

        return normalized;
    });
}

function detectAreasConflict(builderSettings, areasDoc) {
    const hasBuilderSettings = builderSettings && builderSettings.length > 0;
    const hasAreas = areasDoc?.areas && Object.keys(areasDoc.areas).length > 0;

    if (!hasBuilderSettings && hasAreas) {
        return BuilderAreasRelation.NO_BUILDER_YES_AREAS;
    }

    if (!hasBuilderSettings && !hasAreas) {
        return BuilderAreasRelation.NO_BUILDER_NO_AREAS;
    }

    const merged = mergeBuilderSettingsIntoAreas(builderSettings).areas;

    if (deepEqual(merged, areasDoc?.areas)) {
        return BuilderAreasRelation.MATCH;
    }

    return BuilderAreasRelation.CONFLICT;
}

function mergeBuilderSettingsIntoAreas(builderSettings) {
    const areas = {}; // Final merged areas structure used by the map

    const settingAreaRefs = {};
    // Maps each generated area to the builder setting that created it
    // { columnKey: { rowKey: settingIndex } }

    const conflictedAreas = {};
    // Tracks areas involved in naming conflicts
    // { columnKey: { rowKey: true } }

    const conflictedSettingIndices = new Set();
    // Which builder settings produced conflicting areas

    for (const [settingIndex, setting] of builderSettings.entries()) {
        const builtAreas = buildAreas(setting);

        for (const [columnKey, rows] of Object.entries(builtAreas)) {
            areas[columnKey] ??= {};
            settingAreaRefs[columnKey] ??= {};

            for (const [rowKey, values] of Object.entries(rows)) {
                let finalRowKey = rowKey;

                if (finalRowKey in areas[columnKey]) {
                    // Area name already exists → resolve by renaming and mark conflict
                    conflictedAreas[columnKey] ??= {};
                    conflictedAreas[columnKey][rowKey] = true;

                    const existingSettingIndex =
                        settingAreaRefs[columnKey][rowKey];

                    if (existingSettingIndex !== undefined) {
                        conflictedSettingIndices.add(existingSettingIndex);
                    }
                    conflictedSettingIndices.add(settingIndex);

                    let i = 1;
                    finalRowKey = `${rowKey} (${i})`;
                    while (finalRowKey in areas[columnKey]) {
                        i += 1;
                        finalRowKey = `${rowKey} (${i})`;
                    }

                    conflictedAreas[columnKey][finalRowKey] = true;
                }

                areas[columnKey][finalRowKey] = values;
                settingAreaRefs[columnKey][finalRowKey] = settingIndex;
            }
        }
    }

    return {
        areas,
        settingAreaRefs,
        conflictedAreas,
        conflictedSettingIndices,
    };
}

function buildAreaColors({
    conflictedAreas,
    settingAreaRefs,
    highlightedIndex,
}) {
    const conflictColor = { color: "#ff4d4d", alpha: 0.5 };
    const highlightedColor = { color: "#4287f5", alpha: 0.5 };

    const areaColors = {};

    for (const [columnKey, rows] of Object.entries(conflictedAreas)) {
        for (const rowKey of Object.keys(rows)) {
            areaColors[columnKey] ??= {};
            areaColors[columnKey][rowKey] = conflictColor;
        }
    }

    if (highlightedIndex != null) {
        for (const [columnKey, rows] of Object.entries(settingAreaRefs)) {
            for (const [rowKey, settingIndex] of Object.entries(rows)) {
                if (settingIndex === highlightedIndex) {
                    areaColors[columnKey] ??= {};
                    areaColors[columnKey][rowKey] = highlightedColor;
                }
            }
        }
    }

    return areaColors;
}

export default function AreaBuilderPage() {
    const { organizationId, locationId } = useParams();
    const navigate = useNavigate();

    const [showOverwriteModal, setShowOverwriteModal] = React.useState(false);
    const [showClearModal, setShowClearModal] = React.useState(false);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] =
        React.useState(false);

    const [isSaving, setIsSaving] = React.useState(false);

    const [activeKey, setActiveKey] = useState(undefined);

    const [sidebarWidth, setSidebarWidth] = useState(() => {
        const saved = localStorage.getItem("sidebarWidth");
        return saved ? Number(saved) : 400;
    });
    const isResizing = useRef(false);
    const [isResizingState, setIsResizingState] = useState(false);
    const [isHoveringResize, setIsHoveringResize] = useState(false);

    useEffect(() => {
        localStorage.setItem("sidebarWidth", sidebarWidth);
    }, [sidebarWidth]);

    const startResizing = (e) => {
        e.preventDefault();
        e.stopPropagation();

        isResizing.current = true;
        setIsResizingState(true);

        document.body.style.userSelect = "none"; // prevent text selection
    };

    const stopResizing = useCallback(() => {
        isResizing.current = false;
        setIsResizingState(false);

        document.body.style.userSelect = "";
    }, []);

    const resize = useCallback((e) => {
        if (!isResizing.current) return;

        const minWidth = 300;
        const maxWidth = Math.min(900, window.innerWidth - 100);
        const newWidth = Math.min(maxWidth, Math.max(minWidth, e.clientX));
        setSidebarWidth(newWidth);
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => resize(e);
        const handleMouseUp = () => stopResizing();

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [resize, stopResizing]);

    const [pageState, setPageState] = React.useState(PageState.BOOT);

    const [lastHoveredArea, setLastHoveredArea] = useState();
    const [activeBuilderSettings, setActiveBuilderSettings] = React.useState(
        [],
    );
    const [useCurrentAreas, setUseCurrentAreas] = React.useState(false);

    const [areasDoc, areasLoading] = useAreasDocument(
        organizationId,
        locationId,
    );
    const [locBuilderAreasDoc, builderSettingsLoading] =
        useLocationBuilderAreasDocument(organizationId, locationId);
    const locBuilderSettings = useMemo(
        () =>
            normalizeLocationBuilderSettings(locBuilderAreasDoc, defaultValues),
        [locBuilderAreasDoc],
    );

    const conflictState = useMemo(() => {
        if (areasLoading || builderSettingsLoading) return null;
        return detectAreasConflict(locBuilderSettings, areasDoc);
    }, [areasLoading, builderSettingsLoading, locBuilderSettings, areasDoc]);

    useEffect(() => {
        if (pageState !== PageState.BOOT) return;
        if (!conflictState) return;

        switch (conflictState) {
            case BuilderAreasRelation.NO_BUILDER_YES_AREAS:
                setPageState(PageState.CONFLICT);
                break;

            case BuilderAreasRelation.NO_BUILDER_NO_AREAS:
                setActiveBuilderSettings([
                    { ...defaultValues, id: generate() },
                ]);
                setPageState(PageState.DEFAULTS);
                break;

            case BuilderAreasRelation.MATCH:
                setActiveBuilderSettings(locBuilderSettings);
                setPageState(PageState.SETTINGS);
                break;

            case BuilderAreasRelation.CONFLICT:
                setActiveBuilderSettings(locBuilderSettings);
                setPageState(PageState.CONFLICT);
                break;
        }
    }, [pageState, conflictState, locBuilderSettings]);

    const {
        areas,
        settingAreaRefs,
        conflictedAreas,
        conflictedSettingIndices,
    } = React.useMemo(
        () => mergeBuilderSettingsIntoAreas(activeBuilderSettings),
        [activeBuilderSettings],
    );

    const areaColors = React.useMemo(
        () =>
            buildAreaColors({
                conflictedAreas,
                settingAreaRefs,
                highlightedIndex: lastHoveredArea,
            }),
        [conflictedAreas, settingAreaRefs, lastHoveredArea],
    );

    const hasConflicts = Object.keys(conflictedAreas).length > 0;
    const hasUnsavedChanges = !deepEqual(
        locBuilderSettings,
        activeBuilderSettings,
    );

    async function submit({
        clear = false,
        areaSettings = activeBuilderSettings,
    } = {}) {
        setIsSaving(true);
        try {
            await api.setAreasFromJson(
                organizationId,
                locationId,
                clear
                    ? { areas: {} }
                    : {
                          areas: mergeBuilderSettingsIntoAreas(areaSettings)
                              .areas,
                      },
            );

            if (clear)
                await LocationBuilderAreasDocument(
                    organizationId,
                    locationId,
                ).delete();
            else
                await LocationBuilderAreasDocument(
                    organizationId,
                    locationId,
                ).set({ settings: areaSettings }, { merge: true });

            setUseCurrentAreas(false);
            setShowOverwriteModal(false);
            setShowClearModal(false);
            setShowUnsavedChangesModal(false);
            setPageState(PageState.BOOT);
            return true;
        } catch {
            return false;
        } finally {
            setIsSaving(false);
        }
    }

    function newGrid(gridSettings) {
        setActiveBuilderSettings((prev) => [
            ...prev,
            { ...(gridSettings || defaultValues), id: generate() },
        ]);
    }

    function deleteGrid(id) {
        setActiveBuilderSettings((prev) =>
            prev.filter((setting) => id !== setting.id),
        );
    }

    function getGridName(gridSetting) {
        const builtAreas = buildAreas(gridSetting);
        const columnNames = Object.keys(builtAreas);
        if (!columnNames.length) return "Empty grid";

        const firstColumnName = columnNames[0];
        const lastColumnName = columnNames[columnNames.length - 1];

        const firstColumnKeys = Object.keys(builtAreas[firstColumnName]);
        const lastColumnKeys = Object.keys(builtAreas[lastColumnName]);
        const firstRowName = firstColumnKeys[0];
        const lastRowName = lastColumnKeys[lastColumnKeys.length - 1];
        return `${firstColumnName} ${firstRowName} - ${lastColumnName} ${lastRowName}`;
    }

    function SaveButton({ outline = false, leavePage = false }) {
        return (
            <>
                {hasConflicts ? (
                    <Button
                        variant={outline ? "outline-danger" : "danger"}
                        disabled={true}
                    >
                        <FaExclamationTriangle className="mb-1 me-2" />
                        Conflicts
                    </Button>
                ) : (
                    <Button
                        variant={outline ? "outline-success" : "success"}
                        onClick={async () => {
                            const success = await submit({ clear: false });
                            if (success && leavePage) {
                                navigate(
                                    urls.locationSettings(
                                        organizationId,
                                        locationId,
                                    ),
                                );
                            }
                        }}
                        disabled={
                            !hasUnsavedChanges || hasConflicts || isSaving
                        }
                    >
                        <FaSave className="mb-1 me-2" />
                        {isSaving ? "Saving..." : "Save"}
                    </Button>
                )}
            </>
        );
    }

    const CustomToggle = React.forwardRef(({ onClick }, ref) => (
        <button
            type="button"
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
            className="p-0 bg-transparent border-0 d-flex h-100"
        >
            <MdOutlineMoreVert size={20} />
        </button>
    ));
    CustomToggle.displayName = "CustomToggle";

    return (
        <>
            <title>Area Builder | MyCorvus</title>
            <Modal
                show={showOverwriteModal}
                onHide={() => setShowOverwriteModal(false)}
            >
                <Modal.Body>
                    {showOverwriteModal && (
                        <strong className="text-danger">
                            Are you sure you want to overwrite current areas
                            with area builder areas?
                        </strong>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {showOverwriteModal && (
                        <Button
                            variant="danger"
                            disabled={isSaving}
                            onClick={async () => submit({ clear: false })}
                        >
                            {isSaving ? "Overwriting..." : "Overwrite"}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
            <Modal
                show={showClearModal}
                onHide={() => setShowClearModal(false)}
            >
                <Modal.Body>
                    {showClearModal && (
                        <strong className="text-danger">
                            Are you sure you want to clear current areas and
                            area builder areas?
                        </strong>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {showClearModal && (
                        <Button
                            variant="danger"
                            disabled={isSaving}
                            onClick={async () => submit({ clear: true })}
                        >
                            {isSaving ? "Clearing..." : "Clear"}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
            <Modal
                show={showUnsavedChangesModal}
                onHide={() => setShowUnsavedChangesModal(false)}
            >
                <Modal.Body>
                    <div className="d-flex align-items-start gap-3">
                        <FaExclamationTriangle className="text-warning fs-3 mt-1" />
                        <div>
                            <div className="fw-bold fs-4">Unsaved changes</div>
                            <div className="text-muted">
                                {hasConflicts
                                    ? "Are you sure you want to leave?"
                                    : "Do you want to save before leaving?"}
                            </div>
                            {hasConflicts && (
                                <div className="text-muted">
                                    {
                                        "Unsaved changes have conflicts and can't be saved."
                                    }
                                </div>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowUnsavedChangesModal(false)}
                    >
                        <FaPen className="me-2" style={{ marginTop: "-2px" }} />
                        Stay on page
                    </Button>
                    <Button
                        variant="outline-danger"
                        onClick={() =>
                            navigate(
                                urls.locationSettings(
                                    organizationId,
                                    locationId,
                                ),
                            )
                        }
                    >
                        <FaTrash
                            className="me-2"
                            style={{ marginTop: "-2px" }}
                        />
                        Discard
                    </Button>
                    <SaveButton outline={true} leavePage={true} />
                </Modal.Footer>
            </Modal>
            {/* Small screens */}
            <div className="d-sm-none vh-100 d-flex justify-content-center align-items-center text-center p-3">
                <div>
                    <h4>This page requires a larger screen</h4>
                    <p>
                        Please use a tablet or desktop to access the area
                        builder.
                    </p>
                    <Button onClick={() => navigate(-1)}>Go Back</Button>
                </div>
            </div>

            {/* Large screens */}
            <div
                className="d-none d-sm-flex flex-column"
                style={{ height: "100vh" }}
            >
                <Header style={{ maxWidth: "850px" }} />
                <Container
                    fluid
                    className="p-0 d-flex"
                    style={{
                        flexGrow: 1,
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    <div
                        style={{
                            width: sidebarWidth,
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            flexShrink: 0,
                        }}
                    >
                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                pointerEvents: isResizingState
                                    ? "none"
                                    : "auto",
                            }}
                        >
                            <div className="pb-5">
                                {pageState === PageState.BOOT ? (
                                    <div className="d-flex justify-content-center pt-5">
                                        <Spinner
                                            animation="border"
                                            role="status"
                                        >
                                            <span className="visually-hidden">
                                                Loading...
                                            </span>
                                        </Spinner>
                                    </div>
                                ) : (
                                    <>
                                        <Title
                                            title={
                                                <div className="d-inline-flex align-items-center">
                                                    <Button
                                                        variant="link"
                                                        className="p-0 me-2"
                                                        onClick={() => {
                                                            pageState !==
                                                                PageState.CONFLICT &&
                                                            hasUnsavedChanges
                                                                ? setShowUnsavedChangesModal(
                                                                      true,
                                                                  )
                                                                : navigate(
                                                                      urls.locationSettings(
                                                                          organizationId,
                                                                          locationId,
                                                                      ),
                                                                  );
                                                        }}
                                                    >
                                                        <FaAngleLeft
                                                            size={30}
                                                        />
                                                    </Button>
                                                    Area Builder
                                                </div>
                                            }
                                        />
                                        {pageState === PageState.CONFLICT ? (
                                            <Card className="m-2 p-2">
                                                <h2 className="text-danger">
                                                    <strong>Map edited</strong>
                                                </h2>
                                                <p className="text-danger">
                                                    Current areas do not match
                                                    area builder areas
                                                </p>
                                                Show:
                                                <Form.Check
                                                    type="radio"
                                                    name="radioDefault"
                                                    id="radioDefault2"
                                                    label="Current areas"
                                                    value={true}
                                                    checked={!!useCurrentAreas}
                                                    onChange={() =>
                                                        setUseCurrentAreas(true)
                                                    }
                                                />
                                                <Form.Check
                                                    type="radio"
                                                    name="radioDefault"
                                                    id="radioDefault1"
                                                    label="Area builder areas"
                                                    value={false}
                                                    checked={!useCurrentAreas}
                                                    onChange={() =>
                                                        setUseCurrentAreas(
                                                            false,
                                                        )
                                                    }
                                                />
                                                <Button
                                                    variant="danger"
                                                    onClick={() =>
                                                        setShowOverwriteModal(
                                                            true,
                                                        )
                                                    }
                                                    className="mt-2"
                                                >
                                                    <FaFastBackward className="mb-1 me-2" />
                                                    Overwrite current areas with
                                                    area builder areas
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    onClick={() =>
                                                        setShowClearModal(true)
                                                    }
                                                    className="mt-2"
                                                >
                                                    <AiOutlineClear className="mb-1 me-2" />
                                                    Clear current areas and area
                                                    builder areas
                                                </Button>
                                                <ConfigUploadJSONButton
                                                    disabled={false}
                                                    onUpload={async (data) => {
                                                        await submit({
                                                            areaSettings:
                                                                normalizeLocationBuilderSettings(
                                                                    data,
                                                                    defaultValues,
                                                                ),
                                                        });
                                                    }}
                                                    buttonText="Import area builder settings"
                                                    className="mt-2"
                                                    variant="danger"
                                                    uploadIconClassName=""
                                                />
                                            </Card>
                                        ) : (
                                            <>
                                                <div className="mx-2 mb-3">
                                                    <Card.Body className="p-0 d-flex gap-2">
                                                        <SaveButton />
                                                        <CancelButton
                                                            to={urls.locationSettings(
                                                                organizationId,
                                                                locationId,
                                                            )}
                                                        />

                                                        {/* Push everything after this to the right */}
                                                        <div className="ms-auto d-flex gap-2">
                                                            <ConfigDownloadJSONButton
                                                                filename={`area_builder_settings_${locationId}`}
                                                                disabled={false}
                                                                onDownload={() => {
                                                                    return {
                                                                        settings:
                                                                            activeBuilderSettings,
                                                                    };
                                                                }}
                                                                buttonText="Export"
                                                            />
                                                            <ConfigUploadJSONButton
                                                                disabled={false}
                                                                onUpload={(
                                                                    data,
                                                                ) => {
                                                                    setActiveBuilderSettings(
                                                                        normalizeLocationBuilderSettings(
                                                                            data,
                                                                            defaultValues,
                                                                        ),
                                                                    );
                                                                }}
                                                                buttonText="Import"
                                                            />
                                                        </div>
                                                    </Card.Body>
                                                </div>
                                                <Card className="m-2 py-2">
                                                    <Accordion
                                                        alwaysOpen
                                                        id="accordion"
                                                        activeKey={activeKey}
                                                        onSelect={(
                                                            eventKey,
                                                        ) => {
                                                            if (isResizingState)
                                                                return;
                                                            setActiveKey(
                                                                eventKey,
                                                            );
                                                        }}
                                                    >
                                                        {activeBuilderSettings.map(
                                                            (
                                                                setting,
                                                                index,
                                                            ) => (
                                                                <Accordion.Item
                                                                    key={
                                                                        setting.id
                                                                    }
                                                                    className="border-0"
                                                                    eventKey={String(
                                                                        setting.id,
                                                                    )}
                                                                >
                                                                    <div className="d-flex">
                                                                        <style>
                                                                            {`.accordion-button::after {margin-top: 0.5rem;}`}{" "}
                                                                        </style>
                                                                        <Accordion.Button
                                                                            className={`pt-0 pe-0 ps-2 d-flex flex-grow-1 ${index && "border-top"}`}
                                                                            onMouseEnter={() =>
                                                                                setLastHoveredArea(
                                                                                    index,
                                                                                )
                                                                            }
                                                                            onMouseLeave={() =>
                                                                                setLastHoveredArea()
                                                                            }
                                                                            style={{
                                                                                border: "none",
                                                                                boxShadow:
                                                                                    "none",
                                                                                backgroundColor:
                                                                                    "white",
                                                                                alignItems:
                                                                                    "flex-start",
                                                                            }}
                                                                        >
                                                                            <h4 className="my-0 ms-1 me-2">
                                                                                {getGridName(
                                                                                    setting,
                                                                                )}
                                                                            </h4>
                                                                            {conflictedSettingIndices.has(
                                                                                index,
                                                                            ) && (
                                                                                <FaExclamationTriangle
                                                                                    className="mt-2 me-2"
                                                                                    color="orange"
                                                                                />
                                                                            )}
                                                                        </Accordion.Button>

                                                                        <Dropdown
                                                                            align="end"
                                                                            className="align-self-start mt-2"
                                                                            onClick={(
                                                                                e,
                                                                            ) =>
                                                                                e.stopPropagation()
                                                                            }
                                                                        >
                                                                            <Dropdown.Toggle
                                                                                as={
                                                                                    CustomToggle
                                                                                }
                                                                            />
                                                                            <Dropdown.Menu>
                                                                                <Dropdown.Item
                                                                                    className="text-primary d-flex align-items-center"
                                                                                    onClick={() =>
                                                                                        newGrid(
                                                                                            setting,
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <FaCopy className="me-2" />
                                                                                    Duplicate
                                                                                </Dropdown.Item>
                                                                                <Dropdown.Item
                                                                                    className="text-danger d-flex align-items-center"
                                                                                    onClick={() =>
                                                                                        deleteGrid(
                                                                                            setting.id,
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <FaTrash className="me-2" />
                                                                                    Delete
                                                                                </Dropdown.Item>
                                                                            </Dropdown.Menu>
                                                                        </Dropdown>
                                                                    </div>
                                                                    <Accordion.Body className="px-0">
                                                                        <AreaSettingsCard
                                                                            defaults={
                                                                                setting
                                                                            }
                                                                            onChange={(
                                                                                validSettings,
                                                                            ) => {
                                                                                setActiveBuilderSettings(
                                                                                    (
                                                                                        prev,
                                                                                    ) => {
                                                                                        const copy =
                                                                                            [
                                                                                                ...prev,
                                                                                            ];
                                                                                        copy[
                                                                                            index
                                                                                        ] =
                                                                                            {
                                                                                                ...copy[
                                                                                                    index
                                                                                                ],
                                                                                                ...validSettings,
                                                                                            };
                                                                                        return copy;
                                                                                    },
                                                                                );
                                                                            }}
                                                                        />
                                                                        <div className="w-100 d-flex justify-content-around">
                                                                            <Button
                                                                                variant="primary"
                                                                                onClick={() =>
                                                                                    newGrid(
                                                                                        setting,
                                                                                    )
                                                                                }
                                                                            >
                                                                                <FaCopy className="mb-1 me-2" />
                                                                                Duplicate
                                                                                Grid
                                                                            </Button>
                                                                            <Button
                                                                                variant="danger"
                                                                                onClick={() =>
                                                                                    deleteGrid(
                                                                                        setting.id,
                                                                                    )
                                                                                }
                                                                            >
                                                                                <FaTrash className="mb-1 me-2" />
                                                                                Delete
                                                                                Grid
                                                                            </Button>
                                                                        </div>
                                                                    </Accordion.Body>
                                                                </Accordion.Item>
                                                            ),
                                                        )}
                                                    </Accordion>
                                                </Card>
                                                <div className="p-2">
                                                    <Button
                                                        onClick={() =>
                                                            newGrid()
                                                        }
                                                        className="me-2 mb-2"
                                                    >
                                                        <FaPlus className="mb-1 me-2" />
                                                        New Grid
                                                    </Button>
                                                    <Card.Body className="p-0 d-flex gap-2">
                                                        <SaveButton />
                                                        <CancelButton
                                                            to={urls.locationSettings(
                                                                organizationId,
                                                                locationId,
                                                            )}
                                                        />
                                                    </Card.Body>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        {/* Resize handle */}
                        {/* biome-ignore lint/a11y/noStaticElementInteractions: Resize handle uses mouse dragging rather than button-style activation. */}
                        <div
                            onMouseDown={startResizing}
                            onMouseEnter={() => setIsHoveringResize(true)}
                            onMouseLeave={() => setIsHoveringResize(false)}
                            style={{
                                pointerEvents: "auto",
                                width: "8px",
                                position: "absolute",
                                right: "-4px",
                                top: 0,
                                bottom: 0,
                                cursor: "col-resize",
                                zIndex: 10,
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <div
                                style={{
                                    width: "2px",
                                    background: isResizingState
                                        ? "#666"
                                        : isHoveringResize
                                          ? "#999"
                                          : "#ccc",
                                }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            flexGrow: 1,
                            flexShrink: 1,
                            minWidth: 0,
                            overflow: "hidden",
                        }}
                    >
                        <CorvusMap
                            organizationId={organizationId}
                            locationId={locationId}
                            areasToShow={
                                useCurrentAreas ? areasDoc?.areas : areas
                            }
                            areaColors={
                                useCurrentAreas ? undefined : areaColors
                            }
                            inspect
                        />
                    </div>
                </Container>
            </div>
        </>
    );
}
